import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileJson, FileSpreadsheet, RefreshCw, Lock, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const CLASSIFICATIONS = [
  "UI", "Combat", "AI", "Navigation", "Physics", "Rendering",
  "Performance", "Crash", "Mission Logic", "Progression", "Controls",
  "Network", "Audio", "Other"
];

const SEVERITIES = ["Critical", "High", "Medium", "Low"];
const STATUSES = ["Needs Verification", "Replicated", "Verified", "In Progress", "Fixed", "Closed", "Merged"];

export default function ExportPage() {
  const { t } = useLanguage();
  const { isAuthenticated, login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [severity, setSeverity] = useState("");
  const [classification, setClassification] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [exportScope, setExportScope] = useState("active");
  const [includeMerged, setIncludeMerged] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        // Categories API now returns object with types, extract keys as array
        setCategories(Object.keys(response.data));
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (severity && severity !== "all") params.append("severity", severity);
    if (classification && classification !== "all") params.append("classification", classification);
    if (status && status !== "all") params.append("status", status);
    if (category && category !== "all") params.append("category", category);
    if (exportScope) params.append("export_scope", exportScope);
    if (includeMerged) params.append("include_merged", "true");
    return params.toString();
  };

  const handleExport = async (format) => {
    if (!isAuthenticated) {
      toast.error("Login required to export data");
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      const url = `${API}/export/${format}${params ? `?${params}` : ""}`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.status === 401) {
        toast.error("Login required to export data");
        return;
      }
      
      if (!response.ok) throw new Error("Export failed");
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `bugnexus_export.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(t('common.success'));
    } catch (error) {
      console.error("Export error:", error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="export-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">{t('export.title')}</h1>
        <div className="cyber-line mt-2" />
        <p className="text-muted-foreground mt-2 font-body text-sm md:text-base">{t('export.subtitle')}</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Auth Warning */}
        {!isAuthenticated && (
          <Card className="bg-yellow-500/5 border-yellow-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-yellow-400" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-300 font-body">
                    Developer login required to export bug reports.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={login}
                  className="font-mono text-xs border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="export-login-btn"
                >
                  Login
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground">
              {t('export.exportFilters')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <p className="text-sm text-muted-foreground font-body mb-4">
              {t('export.filterDescription')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                  Export Scope
                </label>
                <Select value={exportScope} onValueChange={setExportScope}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="export-scope-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active Only (default)</SelectItem>
                    <SelectItem value="include_closed">Include Closed</SelectItem>
                    {user?.role === 'admin' && (
                      <SelectItem value="all">Include All (Admin)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block flex items-center gap-1">
                  <Gamepad2 className="w-3 h-3" />
                  Category
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="export-category-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.severity')}</label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="export-severity-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.classification')}</label>
                <Select value={classification} onValueChange={setClassification}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="export-classification-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {CLASSIFICATIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.status')}</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="export-status-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Include Merged toggle */}
            <div className="mt-4 flex items-center gap-3">
              <input
                type="checkbox"
                id="include-merged"
                checked={includeMerged}
                onChange={(e) => setIncludeMerged(e.target.checked)}
                className="w-4 h-4 rounded border-border/50 bg-black/20"
                data-testid="include-merged-checkbox"
              />
              <label htmlFor="include-merged" className="text-xs text-muted-foreground font-mono uppercase tracking-wider cursor-pointer">
                Include Merged Bugs
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Export Options */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground">
              {t('export.exportFormat')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* JSON Export */}
              <button
                onClick={() => handleExport("json")}
                disabled={loading || !isAuthenticated}
                className="p-6 bg-muted/30 border border-border/40 rounded-sm hover:border-primary/50 hover:bg-muted/50 transition-colors text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="export-json-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                    <FileJson className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground uppercase tracking-wider">{t('export.json')}</h3>
                    <p className="text-xs text-muted-foreground font-mono mt-1">{t('export.jsonDesc')}</p>
                  </div>
                </div>
              </button>

              {/* CSV Export */}
              <button
                onClick={() => handleExport("csv")}
                disabled={loading || !isAuthenticated}
                className="p-6 bg-muted/30 border border-border/40 rounded-sm hover:border-primary/50 hover:bg-muted/50 transition-colors text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="export-csv-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-sm bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                    <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground uppercase tracking-wider">{t('export.csv')}</h3>
                    <p className="text-xs text-muted-foreground font-mono mt-1">{t('export.csvDesc')}</p>
                  </div>
                </div>
              </button>
            </div>

            {loading && (
              <div className="mt-4 flex items-center justify-center gap-2 text-primary">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="font-mono text-sm">{t('export.generating')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Discord Webhook Info */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground">
              {t('export.discordIntegration')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground font-body mb-4">
              {t('export.discordDesc')}
            </p>
            <div className="bg-black/30 p-4 rounded-sm font-mono text-sm">
              <code className="text-primary">POST {process.env.REACT_APP_BACKEND_URL}/api/discord/webhook</code>
            </div>
            <div className="mt-4 bg-black/30 p-4 rounded-sm">
              <p className="text-xs text-muted-foreground font-mono mb-2">{t('export.requestBody')}:</p>
              <pre className="text-xs text-emerald-400 font-mono overflow-x-auto">
{`{
  "content": "Bug report text here...",
  "username": "DiscordUser#1234",
  "channel_id": "123456789",
  "message_link": "https://discord.com/...",
  "images": ["data:image/png;base64,..."],
  "video_url": "https://..."
}`}
              </pre>
            </div>
            <div className="mt-4 bg-black/30 p-4 rounded-sm">
              <p className="text-xs text-muted-foreground font-mono mb-2">Response:</p>
              <pre className="text-xs text-cyan-400 font-mono overflow-x-auto">
{`{
  "status": "success",
  "bug_id": "uuid",
  "display_code": "2026-02-14-20:45-0012-SHTR-GTWY-PHY-H-DEV2026.02.13.111",
  "title": "Bug title",
  "severity": "High",
  "classification": "Physics",
  "bug_status": "Needs Verification",
  "duplicate_count": 0,
  "reopened": false
}`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
